import { TerraLink } from "../../component/TerraLink/TerraLink"
import { CommonDescription } from "../../component/CommonDescription/CommonDescription"
import { FocusedImage } from "../../component/FocusedImage/FocusedImage"
import { ContentfulImage } from "../../component/ContentfulImage/ContentfulImage"
import {
  layoutHeaderClasses,
  layoutHeaderBigClasses,
  layoutSubheaderClasses,
  layoutEyebrowClasses,
} from "@/components/tailwindHelpers"

import {
  darkTextureWrapper,
  colorSettingDetection,
  renderHeaderConditionally,
} from "../../utils/functions"
import { IMAGE_QUALITY_DESKTOP } from "@/lib/heroImageUtils"

export interface LayoutGeneralProps {
  eyebrow?: string
  header?: string
  headerElement?: "h1" | "h2"
  subheader?: string
  description?: string
  buttons?: TerraLink[]
  image: {
    imageType?: "ImageWithFocalPoint" | "Image" // LayoutGeneral only supports ImageWithFocalPoint
    src?: string
    alt: string
    x?: number
    y?: number
    title?: string
    url?: string
    height: number
    width: number
    focusArea?: string
  }
  orientation?: "left" | "right"
  presetTheme?:
    | "t2-theme-one"
    | "t2-theme-two"
    | "t2-theme-three"
    | "t2-theme-four"
    | "t2-theme-five"
    | "t2-theme-six"
    | "t2-theme-seven"
  isHeaderBig?: boolean
  isWrapped?: boolean
  componentImagePriority?: "high" | "auto" | "low"
  isLCP?: boolean
}

const layoutImageSizes = {
  desktop: {
    width: 520,
    height: 480,
    ratio: {
      width: 13,
      height: 12,
    },
  },
  tablet: {
    width: 480,
    height: 480,
    ratio: {
      width: 1,
      height: 1,
    },
  },
  mobile: {
    width: 520,
    height: 480,
    ratio: {
      width: 13,
      height: 12,
    },
  },
}

export const LayoutGeneral = ({
  eyebrow,
  header,
  headerElement = "h2",
  subheader,
  description,
  presetTheme,
  image,
  orientation,
  isHeaderBig,
  isWrapped,
  buttons,
  componentImagePriority,
  isLCP = false,
}: LayoutGeneralProps) => {
  const wrapperClasses = isWrapped
    ? "w-full px-5 lg:px-20 m-auto max-w-[1440px] t2-container"
    : ""
  const colorSetting = colorSettingDetection(presetTheme || "")
  const mainClasses = `py-[100px] ${presetTheme || ""} ${colorSetting}`
  const headerClasses = `${isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses}`
  const finalHeader = renderHeaderConditionally(
    headerClasses,
    header || "",
    headerElement,
  )

  const componentStyles = darkTextureWrapper(presetTheme || "")
  const isRight = orientation === "right"

  const contentClasses = `t2-layout__content flex flex-wrap -mx-2.5 items-center ${isRight ? "flex-row-reverse" : ""}`
  const primaryClasses = `basis-full max-w-full px-2.5 ml-0 pb-20 lg:pb-0 min-lg:basis-1/2 min-lg:max-w-[50%]`
  const mediaClasses = `basis-full px-2.5 ml-0 min-lg:basis-1/2 min-lg:basis-[41.666%] ${isRight ? "min-lg:mr-[8.333%]" : "min-lg:ml-[8.333%]"} overflow-hidden w-full h-full max-w-[520px] max-h-[480px] min-lg:min-h-[385px] [&_.ter-focused-image-container]:rounded-lg [&_img]:rounded-lg [&_img]:object-cover`

  const renderImage = () => {
    if (image?.imageType === "ImageWithFocalPoint") {
      return (
        <FocusedImage
          focalPointImage={image}
          alt={image?.alt}
          className="t2-lo-general__image"
          quality={IMAGE_QUALITY_DESKTOP}
          imageSizes={layoutImageSizes}
          fullHeightWrapper={true}
          lazyLoad={!isLCP}
          componentImagePriority={componentImagePriority}
        />
      )
    } else if (image?.imageType === "Image") {
      return (
        <ContentfulImage
          src={image.url || image.src || ""}
          alt={image.alt}
          width={layoutImageSizes.desktop.width}
          height={layoutImageSizes.desktop.height}
          focusArea={image?.focusArea}
          className="t2-lo-general__image w-full object-cover rounded-lg"
          componentImagePriority={isLCP ? "high" : componentImagePriority}
        />
      )
    }
  }

  return (
    <div
      className={mainClasses}
      style={componentStyles}
      data-testid="layout-general"
    >
      <div className={wrapperClasses}>
        <div className={contentClasses}>
          <div className={primaryClasses}>
            {eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}
            {finalHeader}
            {subheader && (
              <div className={layoutSubheaderClasses}>{subheader}</div>
            )}
            {description && (
              <CommonDescription
                description={description}
                className="t2-lo-general__description t2-layout__description"
              />
            )}
            {buttons && (
              <div className="flex flex-col md:flex-row mt-10 gap-x-4 gap-y-7">
                {buttons &&
                  buttons.map((btn, index) => (
                    <TerraLink
                      key={index}
                      {...btn}
                      primary={index === 0 ? true : false}
                    />
                  ))}
              </div>
            )}
          </div>
          <div className={mediaClasses}>{renderImage()}</div>
        </div>
      </div>
    </div>
  )
}
